/**
 * Binds siteConfig into the page, switches locale, and handles newsletter submit.
 * Progressive enhancement: the form still POSTs if fetch fails, provided
 * config.js has already set the form action.
 */
(function () {
  const config = window.siteConfig;
  if (!config) return;

  const STORAGE_KEY = "gashuu-locale";
  const legal = config.legal || {};
  const newsletter = config.newsletter || {};
  let activeCopy = {};
  let formBound = false;

  function $(selector) {
    return document.querySelector(selector);
  }

  function $all(selector) {
    return document.querySelectorAll(selector);
  }

  function localeMap() {
    return config.locales && typeof config.locales === "object" ? config.locales : {};
  }

  function supportedLocales() {
    return Object.keys(localeMap());
  }

  function normalizeLang(code) {
    if (!code) return "";
    const lower = String(code).toLowerCase().replace(/_/g, "-");
    if (lower === "jp" || lower.indexOf("ja") === 0) return "ja";
    if (lower.indexOf("nl") === 0) return "nl";
    if (lower.indexOf("en") === 0) return "en";
    return lower.split("-")[0];
  }

  function fallbackLocale() {
    const list = supportedLocales();
    if (config.defaultLocale && list.indexOf(config.defaultLocale) !== -1) {
      return config.defaultLocale;
    }
    return list[0] || "en";
  }

  function detectLocale() {
    const list = supportedLocales();
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = normalizeLang(params.get("lang") || params.get("locale"));
      if (fromUrl && list.indexOf(fromUrl) !== -1) return fromUrl;
    } catch (err) {}

    try {
      const stored = normalizeLang(window.localStorage.getItem(STORAGE_KEY));
      if (stored && list.indexOf(stored) !== -1) return stored;
    } catch (err) {}

    const languages = navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language];
    for (let i = 0; i < languages.length; i++) {
      const found = normalizeLang(languages[i]);
      if (found && list.indexOf(found) !== -1) return found;
    }

    return fallbackLocale();
  }

  function getCopy(locale) {
    const all = localeMap();
    const base = all[fallbackLocale()] || {};
    const chosen = all[locale] || {};
    return {
      htmlLang: chosen.htmlLang || locale || "en",
      switchLabel: chosen.switchLabel || locale,
      tagline: chosen.tagline != null ? chosen.tagline : base.tagline,
      intro: chosen.intro != null ? chosen.intro : base.intro,
      launchNote: chosen.launchNote != null ? chosen.launchNote : base.launchNote,
      country: chosen.country != null ? chosen.country : base.country || "",
      newsletter: Object.assign({}, base.newsletter, chosen.newsletter),
      ui: Object.assign({}, base.ui, chosen.ui),
    };
  }

  function bindText(key, value) {
    $all('[data-bind="' + key + '"]').forEach(function (el) {
      const text = value == null ? "" : String(value).trim();
      el.textContent = text;
      const line = el.closest("[data-hide-empty]");
      if (line) line.hidden = !text;
    });
  }

  function setIntro(text) {
    const root = $('[data-bind="intro"]');
    if (!root) return;
    root.replaceChildren();
    String(text || "")
      .split(/\n\n+/)
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean)
      .forEach(function (part) {
        const p = document.createElement("p");
        p.textContent = part;
        root.appendChild(p);
      });
  }

  /**
   * The contact address is stored reversed and base64 encoded in config.js
   * and only decoded when a visitor clicks. The address is never written
   * into the page until then, so a harvester that reads the HTML, or the
   * config file, finds nothing to match. See SECURITY.md, "Bots and
   * scraping", for what this does and does not stop.
   */
  function unpackEmail() {
    const packed = String(config.contactEmailPacked || "").trim();
    if (!packed) return "";
    let value = "";
    try {
      value = window.atob(packed).split("").reverse().join("");
    } catch (err) {
      return "";
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value) ? value : "";
  }

  function setMailto(copy) {
    const label = (copy && copy.ui && copy.ui.showEmail) || "Show email address";
    $all("[data-bind-mailto]").forEach(function (el) {
      const wrap = el.closest("[data-hide-empty]");
      if (!config.contactEmailPacked) {
        el.hidden = true;
        if (wrap) wrap.hidden = true;
        return;
      }
      el.hidden = false;
      if (wrap) wrap.hidden = false;
      el.textContent = label;
      el.removeAttribute("href");
      el.setAttribute("role", "button");
      el.setAttribute("tabindex", "0");

      const reveal = function (event) {
        event.preventDefault();
        const address = unpackEmail();
        if (!address) return;
        el.textContent = address;
        el.setAttribute("href", "mailto:" + address);
        el.setAttribute("rel", "nofollow");
        el.removeAttribute("role");
        el.removeAttribute("tabindex");
        el.removeEventListener("click", reveal);
        el.removeEventListener("keydown", onKey);
        el.focus();
      };
      const onKey = function (event) {
        if (event.key === "Enter" || event.key === " ") reveal(event);
      };
      el.addEventListener("click", reveal);
      el.addEventListener("keydown", onKey);
    });
  }

  function setMastheadMeta(copy) {
    const parts = [config.edition, copy.launchNote]
      .map(function (value) {
        return value == null ? "" : String(value).trim();
      })
      .filter(Boolean);
    $all('[data-bind="mastheadMeta"]').forEach(function (el) {
      el.textContent = parts.join("  /  ");
      el.hidden = !parts.length;
    });
  }

  function setSocial(links) {
    const nav = $("[data-bind-social]");
    if (!nav) return;
    nav.replaceChildren();
    const items = Array.isArray(links) ? links.filter(function (item) {
      return item && item.url && item.label;
    }) : [];
    if (!items.length) {
      nav.hidden = true;
      return;
    }
    nav.hidden = false;
    items.forEach(function (item) {
      const a = document.createElement("a");
      a.href = item.url;
      a.textContent = item.label;
      a.rel = "noopener noreferrer";
      nav.appendChild(a);
    });
  }

  function setLocalesNav(active) {
    const nav = $("[data-bind-locales]");
    if (!nav) return;
    const list = supportedLocales();
    const all = localeMap();
    nav.replaceChildren();
    if (list.length < 2) {
      nav.hidden = true;
      return;
    }
    nav.hidden = false;
    list.forEach(function (code) {
      const a = document.createElement("a");
      a.href = "?lang=" + encodeURIComponent(code);
      a.setAttribute("data-locale", code);
      a.setAttribute("hreflang", (all[code] && all[code].htmlLang) || code);
      a.setAttribute("lang", (all[code] && all[code].htmlLang) || code);
      a.textContent = (all[code] && all[code].switchLabel) || code;
      if (code === active) a.setAttribute("aria-current", "true");
      nav.appendChild(a);
    });
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function setForm(copy) {
    const form = $("#newsletter-form");
    if (!form) return;

    const emailInput = $("#email");
    const submitButton = form.querySelector('button[type="submit"]');
    const formAction = (newsletter.formAction || "").trim();
    const showForm = newsletter.enabled !== false && formAction.length > 0;

    form.hidden = !showForm;
    if (!showForm) return;

    form.action = formAction;
    form.method = "post";
    form.setAttribute("accept-charset", "UTF-8");

    if (newsletter.emailFieldName && emailInput) {
      emailInput.name = newsletter.emailFieldName;
    }
    if (copy.newsletter.placeholder && emailInput) {
      emailInput.setAttribute("placeholder", copy.newsletter.placeholder);
    }
    if (copy.ui.emailField && emailInput) {
      const fieldLabel = $('label[for="email"]');
      if (fieldLabel) fieldLabel.textContent = copy.ui.emailField;
    }
    if (copy.newsletter.buttonLabel && submitButton) {
      submitButton.textContent = copy.newsletter.buttonLabel;
    }
  }

  function bindForm() {
    const form = $("#newsletter-form");
    if (!form || formBound) return;
    formBound = true;

    const emailInput = $("#email");
    const status = $("#form-status");
    const submitButton = form.querySelector('button[type="submit"]');

    function setStatus(message, isError) {
      if (!status) return;
      status.textContent = message || "";
      status.classList.toggle("is-error", Boolean(isError));
    }

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      if (!emailInput) return;
      const ui = activeCopy.ui || {};
      const messages = activeCopy.newsletter || {};

      const email = emailInput.value.trim();
      emailInput.value = email;
      emailInput.removeAttribute("aria-invalid");

      if (!email || !isValidEmail(email) || (emailInput.checkValidity && !emailInput.checkValidity())) {
        emailInput.setAttribute("aria-invalid", "true");
        setStatus(ui.invalidEmail || "Please enter a valid email address.", true);
        emailInput.focus();
        return;
      }

      if (submitButton) submitButton.disabled = true;
      setStatus(ui.sending || "Sending…", false);

      const body = new FormData(form);

      fetch(form.action, {
        method: "POST",
        body: body,
        headers: { Accept: "application/json" },
      })
        .then(function (response) {
          if (response.ok) {
            form.reset();
            setStatus(messages.successMessage || "Thank you.", false);
            return null;
          }
          return response.json().catch(function () {
            return {};
          }).then(function (data) {
            const message =
              (data && (data.error || data.message)) ||
              ui.error ||
              "Something went wrong. Please try again, or email us.";
            setStatus(String(message), true);
          });
        })
        .catch(function () {
          form.submit();
        })
        .finally(function () {
          if (submitButton) submitButton.disabled = false;
        });
    });
  }

  function render(locale) {
    const copy = getCopy(locale);
    const ui = copy.ui || {};
    activeCopy = copy;

    document.documentElement.lang = copy.htmlLang || locale;
    document.documentElement.setAttribute("data-locale", locale);

    const titleParts = [config.brandName, copy.tagline].filter(Boolean);
    if (titleParts.length) document.title = titleParts.join(", ");

    const description = String(copy.intro || "")
      .replace(/\n+/g, " ")
      .trim();
    const meta = $('meta[name="description"]');
    if (meta && description) meta.setAttribute("content", description);

    bindText("brandName", config.brandName);
    bindText("brandNameSecondary", config.brandNameSecondary);
    bindText("tagline", copy.tagline);
    bindText("labelList", ui.list);
    bindText("labelMail", ui.mail);
    bindText("labelCompany", ui.company);
    bindText("labelKvk", ui.kvk);
    bindText("labelVat", ui.vat);
    bindText("labelAddress", ui.address);
    bindText("labelCountry", ui.country);
    bindText("companyName", legal.companyName);
    bindText("kvkNumber", legal.kvkNumber);
    bindText("vatNumber", legal.vatNumber);
    bindText("address", legal.address);
    bindText("country", copy.country);
    setMastheadMeta(copy);
    setIntro(copy.intro);
    setMailto(copy);
    setSocial(config.social);
    setLocalesNav(locale);
    setForm(copy);

    const localesNav = $("[data-bind-locales]");
    if (localesNav) localesNav.setAttribute("aria-label", ui.language || "Language");
  }

  function remember(locale) {
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
    } catch (err) {}
  }

  function writeUrl(locale) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("lang", locale);
      window.history.replaceState({ locale: locale }, "", url);
    } catch (err) {}
  }

  function applyLocale(locale, updateUrl) {
    const list = supportedLocales();
    const next = list.indexOf(locale) !== -1 ? locale : fallbackLocale();
    remember(next);
    if (updateUrl) writeUrl(next);
    render(next);
  }

  const localesNav = $("[data-bind-locales]");
  if (localesNav) {
    localesNav.addEventListener("click", function (event) {
      const link = event.target.closest("a[data-locale]");
      if (!link) return;
      event.preventDefault();
      applyLocale(link.getAttribute("data-locale"), true);
    });
  }

  window.addEventListener("popstate", function () {
    applyLocale(detectLocale(), false);
  });

  applyLocale(detectLocale(), false);
  bindForm();
})();
